import { makeObservable } from 'mobx';
import isNil from 'lodash/isNil';

import type { PriceData } from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';
import { PriceStatus } from 'chaika-enums';

import { LongList } from '@store/abstract/LongList';

import { Price } from './Price';
import { translate } from '@chaika/i18n/helper';

export const PriceStatusDic = {
    [PriceStatus.Active]: () => translate('activeFeminine'),
    [PriceStatus.Archived]: () => translate('archive'),
    [PriceStatus.Planned]: () => translate('plannedFemenine'),
};

interface FilterParams {
    serviceId?: string[];
    serviceIds?: string[];
    userId?: string[];
    statuses?: PriceStatus[];
}

export class PriceList extends LongList<Price, PriceData, FilterParams> {
    public readonly limit = 50;

    constructor(params?: PriceData[]) {
        super(params);
        makeObservable(this);

        this.filterParams = {
            statuses: [PriceStatus.Active],
        };
    }

    public getNotLoadedPriceServiceIds(serviceIds: string[]): string[] {
        const loadedServiceIds = new Set(
            this.getFilteredValues({ serviceIds, statuses: [PriceStatus.Active] }).map(({ serviceId }) => serviceId),
        );

        return serviceIds.filter(serviceId => !loadedServiceIds.has(serviceId));
    }

    public isPriceLoaded(serviceId: string): boolean {
        return this.values.some(price => price.serviceId === serviceId && price.status === PriceStatus.Active);
    }

    public getPriceByServiceId(serviceId: string): Optional<Price> {
        return this.values.find(price => price.serviceId === serviceId && price.status === PriceStatus.Active);
    }

    public getDoctorPriceByServiceId(serviceId: string, userId: string): Optional<Price> {
        return this.values.find(price =>
            [price.serviceId === serviceId, price.userId === userId, price.status === PriceStatus.Active].every(
                Boolean,
            ),
        );
    }

    public getBasePricesByServiceIds(serviceIds: string[], legalEntityId?: string): Price[] {
        const serviceSet = new Set(serviceIds);
        return this.values.filter(
            price =>
                serviceSet.has(price.serviceId) &&
                price.status === PriceStatus.Active &&
                isNil(price.userId) &&
                (!!legalEntityId ? price.legalEntityIds.includes(legalEntityId) : true),
        );
    }

    public getBasePricesByServiceIdsWithIncludedServices(serviceIds: string[], legalEntityId?: string): Price[] {
        const prices = this.getBasePricesByServiceIds(serviceIds, legalEntityId);
        const includedServiceIds = prices.flatMap(({ includedServices }) => includedServices);
        return includedServiceIds.length > 0
            ? [...prices, ...this.getBasePricesByServiceIds(includedServiceIds, legalEntityId)]
            : prices;
    }

    public getBasePriceByServiceId(serviceId: string, legalEntityId?: string): Optional<Price> {
        return this.values.find(
            price =>
                price.serviceId === serviceId &&
                price.status === PriceStatus.Active &&
                isNil(price.userId) &&
                (!!legalEntityId ? price.legalEntityIds.includes(legalEntityId) : true),
        );
    }

    protected create(params: PriceData): Price {
        return Price.fromData(params);
    }

    protected filterValue(value: Price, { serviceId, statuses, userId, serviceIds }: FilterParams): boolean {
        return (
            this.filterFieldValueByArray(value, userId, 'userId') &&
            this.filterFieldValueByArray(value, serviceId, 'serviceId') &&
            this.filterFieldValueByArray(value, statuses, 'status') &&
            this.filterFieldValueByArray(value, serviceIds, 'serviceId')
        );
    }
}
